import { customElement } from 'lit/decorators.js';
import { WEB_COMPONENT_POLICY_PANEL_GENERAL_INHERITANCE_TABLE } from '../../../../../../../webcomponents.constants';
import { GzPolicyUiSettingsTableWebComponent } from '../../../../../../common/policyPanel/uiSettings/gridTemplate/gridComponents/dataTable';
import { getTableColumns } from './grid-config';
import { html, TemplateResult } from 'lit';
import { GRID_ITEM_FORM_ID } from '../../../../../../common/policyPanel/uiSettings/gridTemplate/gridComponents/base/component/GzPolicyUiSettingsBaseGrid';
import { Inheritance, SectionId } from '@nirvana/shared/dtos/types/policies/common';
import { ValidationResult } from '../../../../../../../ui/gzForm/formField/types/form-validator';
import { InheritanceItemFormValue } from '../../../../utils/uiSettings/general/general.types';
import { translateFn } from '../../../../../../../utils/translator.helper';
import { store } from '@nirvana/redux/store';
import { clearSectionMask } from '../../../../redux/actions';
import { SectionMask } from '../../../../redux/types';
import { PolicyPanelManager } from '../../../../PolicyPanelManager';
import { getAvailableModuleIdsForInheritance } from '../../../../utils/inheritance';
import { GzGeneralInheritanceRulesItemFormWebComponent } from './GzGeneralInheritanceRulesItemFormWebComponent';

@customElement(WEB_COMPONENT_POLICY_PANEL_GENERAL_INHERITANCE_TABLE)
export class GzGeneralInheritanceRulesTableWebComponent extends GzPolicyUiSettingsTableWebComponent<
  Inheritance,
  Inheritance
> {
  protected hasReorderRowAction = false;
  protected hasEditRowAction = false;

  // bind this for access to edited row index
  protected columns = getTableColumns();

  private hiddenInheritanceRules: Inheritance[] = [];

  public setFormValue(value: Inheritance[]) {
    const availableModules = getAvailableModuleIdsForInheritance();
    const filteredValue: Inheritance[] = [];
    value.forEach(item => {
      if (!availableModules.includes(item.moduleId)) {
        this.hiddenInheritanceRules.push(item);
      } else {
        filteredValue.push(item);
      }
    });

    super.setFormValue(filteredValue);
  }

  public getFormValue(): Inheritance[] {
    return [...super.getFormValue(), ...this.hiddenInheritanceRules];
  }

  protected renderGridItemForm(): TemplateResult {
    return html` <gz-policy-panel-general-inheritance-item
      id="${GRID_ITEM_FORM_ID}"
    ></gz-policy-panel-general-inheritance-item>`;
  }

  protected onNewGridItemAdd(item: InheritanceItemFormValue, position?: number): ValidationResult {
    const itemsToAdd: Inheritance[] = [];
    item.sectionIds.forEach(sectionId => {
      itemsToAdd.push({
        policyName: item.policyName,
        policyId: item.policyId,
        sectionId: sectionId,
        moduleId: item.moduleId,
      });
    });

    // check collision
    const itemsInGrid = this.gridComponent.getFormValue();
    const hasCollision = itemsInGrid.some(itemInGrid => {
      return itemsToAdd.some(itemToAdd => {
        return (
          itemInGrid.moduleId === itemToAdd.moduleId &&
          (itemInGrid.sectionId === itemToAdd.sectionId ||
            itemInGrid.sectionId === SectionId.ALL ||
            itemToAdd.sectionId === SectionId.ALL)
        );
      });
    });

    if (hasCollision) {
      const error = translateFn(
        'Unable to add the rule. Your policy already has an inheritance rule with at least one of the selected sections or modules. Please remove the existing rule before adding a new one.'
      );
      this.gridItemForm?.setError(error);
      return {
        valid: false,
        error: error,
      };
    }

    itemsToAdd.forEach(itemToAdd => super.onNewGridItemAdd(itemToAdd));
    PolicyPanelManager.getInstance().setInheritanceMask(itemsToAdd);

    return { valid: true };
  }

  protected onDeleteGridItem(event: CustomEvent) {
    super.onDeleteGridItem(event);
    (this.gridItemForm as GzGeneralInheritanceRulesItemFormWebComponent).resetErrorState();

    store.dispatch(
      clearSectionMask([
        {
          path: {
            moduleId: event.detail.item.moduleId,
            sectionId: event.detail.item.sectionId || SectionId.NONE,
          },
          mask: SectionMask.INHERITANCE,
        },
      ])
    );
  }
}
